import React, { useEffect } from 'react';
import { Box, Container, Divider, makeStyles } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import useAuth from 'src/hooks/useAuth';
import Page from 'src/components/Page';
import useStore from 'src/hooks/useStore';
import { Header } from 'src/components/Headers';
import { SERVICE_CLIENT_AUTH } from 'src/constants/permissions';
import { useHistory, useParams } from 'react-router-dom';
import { ServiceClientVehicleForm } from '../components';
import { TCreateVehicleFormikValues } from '../components/types';
import { useServiceClient } from 'src/hooks';
import { ServiceClient } from 'src/constants/modules/serviceClient';
import { CapitalizeNames } from 'src/utils/capitalize';
import Spinner from 'src/components/Spinner';
import { TServiceClientVehicle } from 'src/interfaces/serviceClient';
import { Alert } from '@material-ui/lab';

const useStyles = makeStyles((theme) => ({
    root: {
        minHeight: '100%',
        paddingTop: theme.spacing(2),
        paddingBottom: theme.spacing(1),
    },
}));

export const EditServiceClientVehicle = () => {
    const classes = useStyles();
    const { t } = useTranslation();
    const { user } = useAuth();
    const { getStores } = useStore();
    const history = useHistory();
    const { serviceClientId, vehicleId } = useParams<{
        serviceClientId: string;
        vehicleId: string;
    }>();
    const { client, loading, updateServiceClientVehicle, getServiceClientById } =
        useServiceClient();

    useEffect(() => {
        if (user._id) getStores();
    }, [user]);

    useEffect(() => {
        if (serviceClientId) getServiceClientById(serviceClientId);
    }, [serviceClientId]);

    const routeVehicleId = String(vehicleId || '');
    const serviceVehicles = Array.isArray(client?.service?.vehicles)
        ? client?.service?.vehicles
        : client?.service?.vehicles
          ? [client.service.vehicles]
          : [];

    type TServiceVehicleItem = {
        _id?: string;
        id?: string;
        vehicleId?: string;
        vehicle?: TServiceClientVehicle | string;
    };

    const selectedVehicleItem = serviceVehicles.find(
        (item: TServiceVehicleItem) => {
            const relationId = String(item?._id || item?.id || '');
            const embeddedVehicleId =
                typeof item?.vehicle === 'object'
                    ? String(item?.vehicle?._id || '')
                    : '';
            const vehicleAsStringId =
                typeof item?.vehicle === 'string' ? String(item.vehicle) : '';
            const vehicleIdField = String(item?.vehicleId || '');

            return (
                relationId === routeVehicleId ||
                embeddedVehicleId === routeVehicleId ||
                vehicleAsStringId === routeVehicleId ||
                vehicleIdField === routeVehicleId
            );
        }
    );

    const selectedVehicle =
        (typeof selectedVehicleItem?.vehicle === 'object' &&
            selectedVehicleItem?.vehicle) ||
        null;

    const initialValues: TCreateVehicleFormikValues = {
        make: selectedVehicle?.make?._id || '',
        model: selectedVehicle?._id || '',
        modelType: selectedVehicle?.modelType || '',
        kilometers: selectedVehicle?.kilometers || 0,
        vin: selectedVehicle?.vin || '',
        year: selectedVehicle?.year || '',
    };

    const handleSubmit = async (values: TCreateVehicleFormikValues) => {
        const response = await updateServiceClientVehicle(
            serviceClientId,
            vehicleId,
            values
        );

        if (response)
            history.push(`/management/service-clients/${serviceClientId}`);
    };

    return (
        <Page className={classes.root} title={t('serviceClient.UpdateVehicle')}>
            <Container maxWidth="lg">
                <Header
                    module={ServiceClient.name}
                    route={`${ServiceClient.route}/${serviceClientId}`}
                    crud={{
                        goBack: true,
                    }}
                    permission={SERVICE_CLIENT_AUTH}
                    title={CapitalizeNames(client?.name)}
                />
                <Divider />
                <Box mt={3}>
                    {loading || !client ? (
                        <Spinner width={45} />
                    ) : (
                        selectedVehicle ? (
                            <ServiceClientVehicleForm
                                mode="edit"
                                initialValues={initialValues}
                                onSubmit={handleSubmit}
                            />
                        ) : (
                            <Alert severity="warning">
                                {t('Messages.ResourceNotFound')}
                            </Alert>
                        )
                    )}
                </Box>
            </Container>
        </Page>
    );
};
